import {
  AccountClient,
  GET_CALENDAR_INFO,
  GET_CALENDAR_INTEGRATED,
  GET_CALENDAR_VIEW_EVENTS,
  TicketClient,
  useCachedQuery,
  useQuery
} from '@nile/graphql/src';
import { SVGIcon } from '@superops/super-icons';
import { Button, Modal, TextStyle } from '@superops/ui-design-system';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import CalendarPage from '../../Pages/SchedulingPage/Pages/CalendarPage';
import {
  ALL_MODULES,
  CustomModal,
  DateTimeFormatter,
  EventEmitter,
  getTranslatedText
} from '@nile/components/src';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';
import AppointmentViewEditContainer from '../../Pages/SchedulingPage/Components/AppointmentViewEditContainer';
import Tippy from '@tippyjs/react';
import { getTransformedChartData } from '@nile/reports/src/modules/charts/utils/chart-transformation';
import { Tooltip } from 'react-clone-tippy';
import {
  COMMON_CONSTANTS,
  PSA_CONSTANTS
} from '@nile/components/src/Constants';
import { RenderTooltip } from '@nile/components/src/Utils/helpers';

const EventComponent = (props) => {
  const { event } = props;

  const [appointmentTippyVisible, setAppointmentTippyVisible] = useState(false);

  const appointmentTippyRef = React.useRef(null);
  const popupParentName = 'appointmentPopup';

  const isPrivateEvent =
    event?.appointment?.sensitivity ===
    PSA_CONSTANTS.SCHEDULING_APPOINTMENT_TYPE.PRIVATE;

  const isNoActionPrivateEvent =
    isPrivateEvent && !event?.appointment?.attendees?.length;

  const closeAllOpenPopups = () => {
    if (
      document.getElementsByClassName(popupParentName) &&
      document.getElementsByClassName(popupParentName)?.length
    ) {
      Array.prototype.forEach.call(
        document.getElementsByClassName(popupParentName),
        (popup) => {
          if (popup?._tippy?.state?.isVisible) {
            popup?._tippy.hide();
          }
        }
      );
    }
  };

  const togglePopOver = () => {
    if (isNoActionPrivateEvent) return;

    if (!appointmentTippyRef.current._tippy.state?.isVisible) {
      closeAllOpenPopups();
      setAppointmentTippyVisible(true);
      appointmentTippyRef.current._tippy.show();
    } else if (appointmentTippyRef.current._tippy.state?.isVisible) {
      appointmentTippyRef.current._tippy.hide();
      setAppointmentTippyVisible(false);
    }
  };

  const intl = useIntl();

  return (
    <Tippy
      content={
        <>
          {appointmentTippyVisible && (
            <div className="pt20">
              <AppointmentViewEditContainer
                eventId={event?.eventId}
                togglePopOver={togglePopOver}
                workItem={{
                  workId: props?.event?.workItem?.workId,
                  module: props?.event?.workItem?.module
                }}
                viewMode
              />
            </div>
          )}
        </>
      }
      maxWidth={600}
      interactive
      placement="left"
      theme="light"
      allowHTML
      appendTo={document.body}
      ref={(node) => {
        appointmentTippyRef.current = node;
      }}
      trigger="manual"
      open={appointmentTippyVisible}
      hideOnClick={false}
      zIndex={500}
    >
      <div
        className={`${popupParentName} mb-10 pointer ${isNoActionPrivateEvent &&
          'cursor-not-allowed'}`}
        onClick={togglePopOver}
      >
        <RenderTooltip
          isVisible={isPrivateEvent}
          tooltip={
            <Tooltip
              distance={35}
              title={getTranslatedText(
                intl,
                isNoActionPrivateEvent
                  ? 'ticket.scheduling.privateEvent.noAction'
                  : 'ticket.scheduling.privateEvent.title'
              )}
              position="top-start"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            />
          }
        >
          <div className="flex g5">
            <div className="mt3">
              <SVGIcon name={isPrivateEvent ? 'Lock' : 'DatePicker'} />
            </div>
            <div>
              <div className="mb-5">
                <TextStyle weight="Medium">
                  {event?.appointment?.subject}
                </TextStyle>
              </div>
              <div className="flex g2 f-12">
                {!props?.hideDay && (
                  <div>
                    <DateTimeFormatter type="date" date={event?.startTime} />
                  </div>
                )}

                <div className="flex g2">
                  <div>
                    <DateTimeFormatter type="time" date={event?.startTime} />
                  </div>
                  <div> - </div>
                  <div>
                    <DateTimeFormatter type="time" date={event?.endTime} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </RenderTooltip>
      </div>
    </Tippy>
  );
};

const TicketSchedulingCard = (props) => {
  const {
    loading: calendarInfoLoading,
    error: calendarInfoError,
    data: calendarInfo
  } = useQuery(GET_CALENDAR_INTEGRATED, {
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    client: AccountClient
  }); // For integrated date

  const calendarIntegrated =
    calendarInfo?.getLoggedInContext?.user?.calendarIntegrated;

  const [events, setEvents] = useState([]);
  const [calendarActive, setCalendarActive] = useState(false);
  const [currentEvents, setCurrentEvents] = useState([]);
  const [previousEvents, setPreviousEvents] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  const formattedWorkItem = {
    workId: props?.workItem?.workId || props?.workItem?.ticketId,
    module: props?.workItem?.ticketId
      ? ALL_MODULES.ticket
      : ALL_MODULES.project,
    client: props?.workItem?.client,
    subject: props?.workItem?.subject,
    displayId: props?.workItem?.displayId,
    requester: props?.workItem?.requester
  };

  const calendarVariables = {
    workItem: {
      workId: props?.workItem?.workId,
      module: props?.module || 'TICKET'
    },
    condition: {
      joinOperator: 'AND',
      operands: [
        {
          attribute: 'eventType',
          operator: 'is',
          value: 'APPOINTMENT'
        },
        {
          attribute: 'appointment.syncStatus',
          operator: 'is',
          value: 'SUCCESS'
        },
        {
          attribute: 'workItem.workId',
          operator: 'is',
          value: props?.workItem?.workId
        }
      ]
    }
  };

  const getEventType = (inputDate) => {
    const currentDate = dayjs().startOf('day');
    const providedDate = dayjs(inputDate).startOf('day');

    if (providedDate.isSame(currentDate)) {
      return 'Today';
    } else if (providedDate.isBefore(currentDate)) {
      return 'Previous';
    } else {
      return 'Upcoming';
    }
  };

  const {
    loading,
    data: calendarEvents,
    refetch: refetchCalendarView
  } = useQuery(GET_CALENDAR_VIEW_EVENTS, {
    client: TicketClient,
    variables: {
      input: calendarVariables
    },
    fetchPolicy: COMMON_CONSTANTS.CACHE_POLICIES.NO_CACHE,
    onCompleted(res) {
      const allEvents = res?.getCalendarView?.flatMap((el) => el?.events);

      const today = [];
      const upcoming = [];
      const previous = [];

      allEvents.forEach((el) => {
        const type = getEventType(el?.startTime);
        const event = { ...el, type: type };
        if (type === 'Today') {
          today.push(event);
        } else if (type === 'Previous') {
          previous.push(event);
        } else if (type === 'Upcoming') {
          upcoming.push(event);
        }
      });

      setEvents(allEvents || []);
      setPreviousEvents(previous);
      setCurrentEvents(today);
      setUpcomingEvents(upcoming);
    }
  });

  const togglePopover = () => {
    setCalendarActive(!calendarActive);
  };

  return (
    <>
      <div className="flex actr fx-sb mb-10">
        <div>
          <TextStyle weight="SemiBold">
            <FormattedMessage id="scheduling.workitem.widget.title" />
          </TextStyle>
        </div>
        {calendarIntegrated && (
          <div>
            <Button
              plain
              icon={<SVGIcon name="Add" />}
              onClick={() => {
                setCalendarActive(!calendarActive);
              }}
            />
          </div>
        )}
      </div>

      {isEmpty(events) ? (
        <>
          <TextStyle variation="subdued">
            <FormattedMessage id="scheduling.workitem.widget.no.event.text" />
          </TextStyle>
        </>
      ) : (
        <>
          {!isEmpty(previousEvents) && (
            <div className="mb-10">
              <div className="mb5 mt5 uppercase">
                <TextStyle variation="subdued">
                  <span className="f-12">
                    <FormattedMessage id="scheduling.workitem.widget.previous.event.title" />
                  </span>
                </TextStyle>
              </div>
              <div>
                {previousEvents.map((event) => (
                  <EventComponent event={event} />
                ))}
              </div>
            </div>
          )}

          {!isEmpty(currentEvents) && (
            <div className="mb-10">
              <div className="mb5 mt5 uppercase">
                <TextStyle variation="subdued">
                  <span className="f-12">
                    <FormattedMessage id="scheduling.workitem.widget.today.event.title" />
                  </span>
                </TextStyle>
              </div>
              <div>
                {currentEvents.map((event) => (
                  <EventComponent event={event} hideDay />
                ))}
              </div>
            </div>
          )}

          {!isEmpty(upcomingEvents) && (
            <div className="mb-10">
              <div className="mb5 mt5 uppercase">
                <TextStyle variation="subdued">
                  <span className="f-12">
                    <FormattedMessage id="scheduling.workitem.widget.upcoming.event.title" />
                  </span>
                </TextStyle>
              </div>
              <div>
                {upcomingEvents.map((event) => (
                  <EventComponent event={event} />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <CustomModal
        open={calendarActive}
        onClose={() => {
          setCalendarActive(false);
        }}
        zIndex={500}
        className="ticket-scheduling-modal"
        locked
      >
        <div className="ticket-scheduling-wrapper">
          <CalendarPage
            workItem={formattedWorkItem}
            dispatcherTechnicians={props?.dispatcherTechnicians}
            attendeesFromWorkItem={props?.attendeesFromWorkItem}
            onClose={() => {
              setCalendarActive(false);
            }}
            hideCalendarSettings
            mixPanelEventSource={props?.module || ALL_MODULES.ticket}
          />
        </div>
      </CustomModal>
    </>
  );
};

export default TicketSchedulingCard;
